/**
 * useFaceDetection Hook
 *
 * Provides face detection functionality using face-api.js.
 * Handles model loading, continuous detection, and descriptor extraction.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import * as faceapi from 'face-api.js';
import { loadFaceModels, areModelsLoaded } from '../lib/modelLoader';
import type { FaceDetectionResult, UseFaceDetectionReturn } from '../types';

console.log('[ZK Face Detection] Module loaded, faceapi available:', !!faceapi);

// ============================================================================
// Constants
// ============================================================================

const DETECTION_INTERVAL = 100; // ms between detection attempts
const MIN_FACE_CONFIDENCE = 0.5;

// ============================================================================
// Hook
// ============================================================================

export function useFaceDetection(): UseFaceDetectionReturn {
  console.log('[ZK Face Detection] Hook initialized');

  // State
  const [isModelLoaded, setIsModelLoaded] = useState(() => {
    const loaded = areModelsLoaded();
    console.log('[ZK Face Detection] Initial model loaded state:', loaded);
    return loaded;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedFace, setDetectedFace] = useState<FaceDetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const detectionIntervalRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, []);

  /**
   * Load face detection models
   */
  const loadModels = useCallback(async () => {
    console.log('[ZK Face Detection] loadModels called, isModelLoaded:', isModelLoaded);
    if (isModelLoaded) {
      console.log('[ZK Face Detection] Models already loaded, skipping');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[ZK Face Detection] Starting to load face models...');
      await loadFaceModels();
      console.log('[ZK Face Detection] Face models loaded successfully!');
      if (isMountedRef.current) {
        setIsModelLoaded(true);
      }
    } catch (err) {
      console.error('[ZK Face Detection] Failed to load models:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load face detection models');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isModelLoaded]);

  /**
   * Detect face in current video frame
   * Note: Uses areModelsLoaded() instead of isModelLoaded state to avoid stale closure issues
   */
  const detectFace = useCallback(async (): Promise<FaceDetectionResult | null> => {
    if (!videoRef.current) {
      console.log('[ZK Face Detection] detectFace: No video ref');
      return null;
    }

    // Use global function instead of state to avoid stale closure in interval
    if (!areModelsLoaded()) {
      console.log('[ZK Face Detection] detectFace: Models not loaded (checked via areModelsLoaded())');
      return null;
    }

    try {
      // Check if faceapi is available
      if (typeof faceapi === 'undefined') {
        console.error('[ZK Face Detection] faceapi is undefined! Library not loaded.');
        return null;
      }

      // Run face detection with landmarks and descriptors
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({
          inputSize: 320,
          scoreThreshold: MIN_FACE_CONFIDENCE,
        }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        // Only log occasionally to avoid spam
        if (Math.random() < 0.1) {
          console.log('[ZK Face Detection] No face detected in frame');
        }
        return null;
      }

      // Extract results
      const { detection: faceDetection, descriptor } = detection;
      const box = faceDetection.box;

      console.log('[ZK Face Detection] Face detected! Confidence:', faceDetection.score.toFixed(2));

      return {
        descriptor: new Float32Array(descriptor),
        confidence: faceDetection.score,
        boundingBox: {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
        },
      };
    } catch (err) {
      console.error('[ZK Face Detection] Face detection error:', err);
      return null;
    }
  }, []);

  /**
   * Start continuous face detection
   */
  const startDetection = useCallback(
    async (videoElement: HTMLVideoElement) => {
      console.log('[ZK Face Detection] startDetection called');

      // Prevent multiple intervals
      if (detectionIntervalRef.current) {
        console.log('[ZK Face Detection] Detection already running, skipping');
        return;
      }

      console.log('[ZK Face Detection] Video element:', videoElement);
      console.log('[ZK Face Detection] Video readyState:', videoElement.readyState);
      console.log('[ZK Face Detection] Video dimensions:', videoElement.videoWidth, 'x', videoElement.videoHeight);

      // Load models if not already loaded
      if (!areModelsLoaded()) {
        console.log('[ZK Face Detection] Models not loaded, loading now...');
        await loadModels();
      }

      // Store video reference
      videoRef.current = videoElement;
      setIsDetecting(true);
      setError(null);

      console.log('[ZK Face Detection] Starting detection loop (interval:', DETECTION_INTERVAL, 'ms)');

      // Start detection loop
      let detectionCount = 0;
      detectionIntervalRef.current = window.setInterval(async () => {
        detectionCount++;
        if (detectionCount <= 5 || detectionCount % 50 === 0) {
          console.log('[ZK Face Detection] Detection loop iteration', detectionCount);
        }
        const result = await detectFace();

        if (isMountedRef.current) {
          setDetectedFace(result);
        }
      }, DETECTION_INTERVAL);
    },
    [loadModels, detectFace]
  );

  /**
   * Stop face detection
   */
  const stopDetection = useCallback(() => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }

    videoRef.current = null;
    setIsDetecting(false);
    setDetectedFace(null);
  }, []);

  /**
   * Capture the current face descriptor
   */
  const captureDescriptor = useCallback(async (): Promise<Float32Array | null> => {
    if (!videoRef.current || !areModelsLoaded()) {
      setError('Face detection not ready');
      return null;
    }

    try {
      const result = await detectFace();

      if (!result) {
        setError('No face detected. Please position your face in the frame.');
        return null;
      }

      return result.descriptor;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture face');
      return null;
    }
  }, [detectFace]);

  return {
    isModelLoaded,
    isLoading,
    isDetecting,
    detectedFace,
    error,
    startDetection,
    stopDetection,
    captureDescriptor,
  };
}

// ============================================================================
// Utility Hook: Camera Stream
// ============================================================================

/**
 * Hook to manage camera stream
 */
export function useCameraStream() {
  console.log('[ZK Camera] useCameraStream hook initialized');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const startCamera = useCallback(async () => {
    console.log('[ZK Camera] startCamera called');
    setIsLoading(true);
    setError(null);

    try {
      console.log('[ZK Camera] Requesting camera access...');
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Front camera
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });

      console.log('[ZK Camera] Camera access granted!');
      console.log('[ZK Camera] Stream tracks:', mediaStream.getTracks().map(t => ({ kind: t.kind, label: t.label, enabled: t.enabled })));

      setStream(mediaStream);
      return mediaStream;
    } catch (err) {
      console.error('[ZK Camera] Camera access error:', err);
      const errorMessage =
        err instanceof Error
          ? err.name === 'NotAllowedError'
            ? 'Camera access denied. Please enable camera permissions.'
            : err.message
          : 'Failed to access camera';

      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return {
    stream,
    error,
    isLoading,
    startCamera,
    stopCamera,
  };
}
